import React from 'react'
import { Typography, Paper, Box, Stack, Select, MenuItem, FormControl } from '@mui/material'
import Chart from 'react-apexcharts'
import moment from 'moment'
import { useTheme } from '@mui/material'

const StatsCharts = ({
  selectedCheckinChartDataDate,
  timePeriod,
  activitiesDateList,
  checkinChartDataDaily,
  verificationChartData,
  normalVerified,
  normalPercentage,
  approvedByStaff,
  approvedPercentage,
  notYetVerified,
  notVerifiedPercentage,
  onCheckInDateChange,
  onTimePeriodChange,
}) => {
  const theme = useTheme()

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
      sx={{ mb: { xs: 2.5, sm: 3, md: 3.5 } }}>
      {/* Participants Bar Chart */}
      <Box sx={{ width: { xs: '100%', md: '66.666%' }, flex: { md: '2 1 0' } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 2,
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 1)' : 'rgba(18, 18, 18, 1)',
            border: (theme) =>
              `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
            boxShadow: (theme) =>
              theme.palette.mode === 'light'
                ? '0 2px 8px rgba(0, 0, 0, 0.08)'
                : '0 2px 8px rgba(0, 0, 0, 0.3)',
            height: '100%',
          }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: { xs: 2, sm: 2.5, md: 3 },
              flexWrap: 'wrap',
              gap: { xs: 1.5, sm: 2 },
            }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              }}>
              Participants
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 1, sm: 1.5 },
                flexWrap: 'wrap',
              }}>
              {activitiesDateList.length > 0 && (
                <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 140, md: 160 } }}>
                  <Select
                    value={selectedCheckinChartDataDate}
                    onChange={onCheckInDateChange}
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      borderRadius: 1,
                    }}>
                    {activitiesDateList.map((date) => (
                      <MenuItem key={date} value={date}>
                        {moment(date).format('DD MMM YYYY')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 120, md: 140 } }}>
                <Select
                  value={timePeriod}
                  onChange={onTimePeriodChange}
                  sx={{
                    fontSize: {
                      xs: '0.8125rem',
                      sm: '0.875rem',
                      md: '0.9375rem',
                    },
                    borderRadius: 1,
                  }}>
                  <MenuItem value="Daily">Daily</MenuItem>
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Chart
            options={checkinChartDataDaily.options}
            series={checkinChartDataDaily.series}
            type="bar"
            height={350}
          />
        </Paper>
      </Box>

      {/* Verification Status Donut Chart */}
      <Box sx={{ width: { xs: '100%', md: '33.333%' }, flex: { md: '1 1 0' } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 2,
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 1)' : 'rgba(18, 18, 18, 1)',
            border: (theme) =>
              `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
            boxShadow: (theme) =>
              theme.palette.mode === 'light'
                ? '0 2px 8px rgba(0, 0, 0, 0.08)'
                : '0 2px 8px rgba(0, 0, 0, 0.3)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              mb: { xs: 2, sm: 2.5, md: 3 },
            }}>
            Verification Status
          </Typography>

          {/* Legend */}
          <Stack spacing={{ xs: 1, sm: 1.25, md: 1.5 }} sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: '#9C27B0',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  color: 'text.primary',
                }}>
                Normal: {normalVerified?.toLocaleString() || '0'} ({normalPercentage}%)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: '#BA68C8',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  color: 'text.primary',
                }}>
                Approved by staff: {approvedByStaff?.toLocaleString() || '0'} ({approvedPercentage}
                %)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: '#BDBDBD',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  color: 'text.primary',
                }}>
                Not yet verified: {notYetVerified?.toLocaleString() || '0'} ({notVerifiedPercentage}
                %)
              </Typography>
            </Box>
          </Stack>

          {/* Donut Chart */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Chart
              type="donut"
              options={verificationChartData.options}
              series={verificationChartData.series}
              height={250}
            />
          </Box>
        </Paper>
      </Box>
    </Stack>
  )
}

export default StatsCharts
